import { useProject } from '@/features/project/_hooks/project';
import { useRecursiveOrganizationFilteredByKey } from '@/features/tenant-manager/hooks';
import type { permissionType } from '@/features/tenant-manager/models';
import { useId } from '@/hooks';
import { isPS } from '@/utils';
import { type FC, type PropsWithChildren, useMemo } from 'react';
import type { z } from 'zod';
import { useMe } from '../_hooks/me';

const PublishedProjectGuard: FC<
  PropsWithChildren & {
    projectId: string;
    additionalAccessibleProjectRole:
      | z.infer<typeof permissionType>[]
      | undefined;
  }
> = ({ children, projectId, additionalAccessibleProjectRole }) => {
  const project = useProject(projectId);
  const me = useMe();

  const {
    data: organizationIdsForLoginUser,
    isLoading: isOrganizationIdsForLoginUserLoading,
  } = useRecursiveOrganizationFilteredByKey({
    params: {
      ids: me.data?.organizationIds ?? [],
    },
    options: {
      reverse: true,
      filterBy: 'id',
      separator: undefined,
    },
    select: (data) => data.flat(),
  });

  const isLoading =
    project.isLoading || me.isLoading || isOrganizationIdsForLoginUserLoading;

  const hasPermission = useMemo<boolean>(() => {
    const userPermission = me.data?.role.resourceGroupPermissions.project;

    if (!project.data || isLoading) {
      return false;
    }

    if (!project.data.isPublished) {
      return true;
    }

    const hasPermission =
      userPermission === 'privilege' ||
      (additionalAccessibleProjectRole &&
        userPermission &&
        additionalAccessibleProjectRole.includes(userPermission));

    const isUserBelongsToProjectOrganizations =
      !!organizationIdsForLoginUser?.includes(project.data.organizationId);

    const isUserBelongsToProjectRelatedOrganizations =
      !!project.data.relatedOrganizationIds.some((id) =>
        organizationIdsForLoginUser?.includes(id),
      );

    const isUserBelongsToOtherResponsibleOrganizations =
      !!project.data.otherResponsibleOrganizationIds.some((id) =>
        organizationIdsForLoginUser?.includes(id),
      );

    const isUserBelongsToAdditionalJurisdictionalOrganizations =
      !!project.data.fundAdditionalAttributes?.additionalJurisdictionalUsers?.some(
        (obj) => organizationIdsForLoginUser?.includes(obj.organizationId),
      );

    return (
      hasPermission ||
      isUserBelongsToProjectOrganizations ||
      isUserBelongsToProjectRelatedOrganizations ||
      isUserBelongsToOtherResponsibleOrganizations ||
      isUserBelongsToAdditionalJurisdictionalOrganizations
    );
  }, [
    isLoading,
    additionalAccessibleProjectRole,
    me.data?.role.resourceGroupPermissions.project,
    organizationIdsForLoginUser,
    project.data,
  ]);

  return hasPermission ? <>{children}</> : null;
};

export const PublishedProjectMutableGuard: FC<
  PropsWithChildren & {
    additionalAccessibleProjectRole?: z.infer<typeof permissionType>[];
  }
> = ({ children, additionalAccessibleProjectRole }) => {
  const { id: projectId } = useId();

  // NOTE: For PS, to not let them edit.
  if (isPS() || !projectId) {
    return null;
  }

  return (
    <PublishedProjectGuard
      projectId={projectId}
      additionalAccessibleProjectRole={additionalAccessibleProjectRole}
    >
      {children}
    </PublishedProjectGuard>
  );
};
